import { useState, useEffect } from "react";

import { useAppDispatch } from "@/store/hooks";
import { setLocation } from "@/store/slices/config";

interface UseGooglePlacesProps {
  apiKey: string;
}

interface LatLng {
  lat: number;
  lng: number;
}

export const useGooglePlaces = ({ apiKey }: UseGooglePlacesProps) => {
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [searchResults, setSearchResults] = useState<
    google.maps.places.AutocompletePrediction[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useAppDispatch();

  useEffect(() => {
    const loadGooglePlacesScript = () => {
      return new Promise<void>((resolve) => {
        if (window.google) {
          setIsScriptLoaded(true);
          resolve();
          return;
        }

        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,geocoding`;
        script.async = true;
        script.defer = true;
        script.onload = () => {
          setIsScriptLoaded(true);
          resolve();
        };
        document.head.appendChild(script);
      });
    };

    loadGooglePlacesScript();
  }, [apiKey]);

  const waitForScriptLoad = async () => {
    if (!isScriptLoaded) {
      await new Promise<void>((resolve) => {
        const checkScript = setInterval(() => {
          if (window.google) {
            setIsScriptLoaded(true);
            clearInterval(checkScript);
            resolve();
          }
        }, 100);
      });
    }
  };

  const searchPlaces = async (input: string) => {
    if (!input) {
      setSearchResults([]);
      return;
    }

    await waitForScriptLoad();
    setIsLoading(true);

    const service = new window.google.maps.places.AutocompleteService();
    service.getPlacePredictions(
      {
        input,
      },
      (predictions, status) => {
        setIsLoading(false);
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          predictions
        ) {
          setSearchResults(predictions);
        } else {
          setSearchResults([]);
        }
      }
    );
  };

  const getPlaceDetails = async (
    placeId: string
  ): Promise<google.maps.places.PlaceResult> => {
    await waitForScriptLoad();

    return new Promise((resolve, reject) => {
      const service = new window.google.maps.places.PlacesService(
        document.createElement("div")
      );

      service.getDetails(
        {
          placeId,
          fields: ["geometry", "formatted_address", "address_components"],
        },
        (place, status) => {
          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            place
          ) {
            resolve(place);
          } else {
            reject(new Error("Failed to get place details"));
          }
        }
      );
    });
  };

  const getAddressFromCoordinates = async (
    coordinates: LatLng
  ): Promise<string> => {
    await waitForScriptLoad();

    return new Promise((resolve, reject) => {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: coordinates }, (results, status) => {
        if (
          status === window.google.maps.GeocoderStatus.OK &&
          results &&
          results[0]
        ) {
          dispatch(setLocation(results[0].formatted_address));
          resolve(results[0].formatted_address);
        } else {
          let errorMessage = "Failed to get address from coordinates";

          switch (status) {
            case window.google.maps.GeocoderStatus.OVER_QUERY_LIMIT:
              errorMessage = "Geocoding query limit exceeded";
              break;
            case window.google.maps.GeocoderStatus.REQUEST_DENIED:
              errorMessage =
                "Geocoding request denied. Please check your API key";
              break;
            case window.google.maps.GeocoderStatus.INVALID_REQUEST:
              errorMessage = "Invalid geocoding request";
              break;
            case window.google.maps.GeocoderStatus.UNKNOWN_ERROR:
              errorMessage = "Unknown error occurred during geocoding";
              break;
            case window.google.maps.GeocoderStatus.ZERO_RESULTS:
              errorMessage = "No results found for the given coordinates";
              break;
          }

          reject(new Error(errorMessage));
        }
      });
    });
  };

  return {
    isScriptLoaded,
    searchResults,
    isLoading,
    searchPlaces,
    getPlaceDetails,
    getAddressFromCoordinates,
  };
};
